import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "../../../shared/components/Sidebar";
import BackButton from "../../../shared/components/Backbtn";
import "./post.css";
import { doc, getDoc, deleteDoc, updateDoc, arrayUnion, arrayRemove, collection, getDocs, query, orderBy, addDoc } from "firebase/firestore";
import { useFirebase } from "../../../context/Firebase";
import { Button, Avatar, TextField, Divider } from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ShareIcon from "@mui/icons-material/Share";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useAuthState } from "react-firebase-hooks/auth";
import { firebaseAuth } from "../../../context/Firebase";
import { getPostContent, renderContentWithHashtags, getPostImages } from "../../../shared/utils/postHelpers";
import { getDefaultImage } from "../../../shared/utils/imageHelpers";

const PostDetails = () => {
  const { postId } = useParams();
  const { db } = useFirebase();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [authUser] = useAuthState(firebaseAuth);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const postRef = doc(db, "posts", postId);
        const postSnap = await getDoc(postRef);
        
        if (postSnap.exists()) {
          setPost({ id: postSnap.id, ...postSnap.data() });
        } else {
          console.error("Post not found");
          setPost(null);
        }
      } catch (err) {
        console.error("Error fetching post:", err);
        setPost(null);
      } finally {
        setLoading(false);
      }
    };

    const fetchComments = async () => {
      try {
        const commentsRef = collection(db, "posts", postId, "comments");
        const q = query(commentsRef, orderBy("createdAt", "desc"));
        const snapshot = await getDocs(q);
        const commentsData = snapshot.docs.map(doc => {
          const data = doc.data();
          // Normalize comment data to handle both schemas
          return {
            id: doc.id,
            text: data.text || data.commentText || "",
            userName: data.userName || data.userObject?.name || "Anonymous",
            userProfilePic: data.userProfilePic || data.userObject?.picture || "",
            userId: data.userId || "",
            userGender: data.userGender || data.userObject?.gender || "",
            createdAt: data.createdAt,
          };
        });
        setComments(commentsData);
      } catch (err) {
        console.error("Error fetching comments:", err);
      }
    };

    if (postId) {
      fetchPost();
      fetchComments();
    }
  }, [db, postId]);

  const handleDelete = async () => {
    if (window.confirm("Delete this post?")) {
      try {
        await deleteDoc(doc(db, "posts", postId));
        navigate("/post");
      } catch (err) {
        console.error("Error deleting post:", err);
      }
    }
  };

  const toggleLike = async () => {
    if (!authUser || !post) return;
    const postRef = doc(db, "posts", postId);
    const isLiked = post.likes?.includes(authUser.uid);
    
    setPost(prev => ({
      ...prev,
      likes: isLiked 
        ? prev.likes.filter(id => id !== authUser.uid)
        : [...(prev.likes || []), authUser.uid]
    }));

    await updateDoc(postRef, {
      likes: isLiked ? arrayRemove(authUser.uid) : arrayUnion(authUser.uid),
    });
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !authUser) return;

    try {
      const commentsRef = collection(db, "posts", postId, "comments");
      
      // Fetch user data to get gender for avatar fallback
      const userDocRef = doc(db, "users", authUser.uid);
      const userDoc = await getDoc(userDocRef);
      const userData = userDoc.exists() ? userDoc.data() : null;
      
      const commentData = {
        text: newComment,
        commentText: newComment, // For compatibility with mobile app
        userId: authUser.uid,
        userName: userData?.name || authUser.displayName || "Anonymous",
        userProfilePic: userData?.picture || authUser.photoURL || "",
        userGender: userData?.gender || "",
        userObject: { // For compatibility with mobile app
          name: userData?.name || authUser.displayName || "Anonymous",
          email: userData?.email || authUser.email || "",
          picture: userData?.picture || authUser.photoURL || "",
        },
        createdAt: new Date(),
      };
      
      const docRef = await addDoc(commentsRef, commentData);
      
      // Add to local state immediately with normalized format
      setComments(prev => [{
        id: docRef.id,
        text: newComment,
        userName: commentData.userName,
        userProfilePic: commentData.userProfilePic,
        userId: authUser.uid,
        userGender: commentData.userGender,
        createdAt: new Date(),
      }, ...prev]);
      
      setNewComment("");
    } catch (err) {
      console.error("Error adding comment:", err);
    }
  };

  if (loading) {
    return (
      <div className="loader">
        <span>Loading...</span>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="dashboard">
        <Sidebar />
        <div className="dashboard--content">
          <p>Post not found</p>
        </div>
      </div>
    );
  }

  const postContent = getPostContent(post);
  const postImages = getPostImages(post);
  const isLiked = post.likes?.includes(authUser?.uid);

  return (
    <div className="dashboard">
      <Sidebar />
      <div className="dashboard--content" style={{ backgroundColor: 'var(--app-body-bg)', padding: '20px' }}>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "16px", marginBottom: "20px" }}>
          <BackButton tooltip="Back" />
          {authUser && post.userId === authUser.uid && (
            <>
              <Button 
                variant="outlined" 
                startIcon={<EditIcon />}
                onClick={() => navigate(`/edit-post/${postId}`)}
              >
                Edit
              </Button>
              <Button 
                variant="outlined" 
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
              >
                Delete
              </Button>
            </>
          )}
        </div>

        <div style={{ maxWidth: '680px', margin: '0 auto' }}>
          <div style={{
            backgroundColor: 'var(--card-bg)',
            borderRadius: '8px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            marginBottom: '20px',
            border: '1px solid var(--border-color)'
          }}>
            {/* Post Header */}
            <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Avatar 
                  src={post.userProfilePic || post.myUser?.picture || (post.userGender ? getDefaultImage(post.userGender) : null)}
                  sx={{ width: 40, height: 40 }}
                >
                  {(post.userName || post.myUser?.name)?.[0]?.toUpperCase() || 'U'}
                </Avatar>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '15px' }}>
                    {post.userName || post.myUser?.name || "Unknown User"}
                  </div>
                  <div style={{ fontSize: '13px', color: '#65676b' }}>
                    {post.createAt?.seconds ? new Date(post.createAt.seconds * 1000).toLocaleString() : 'Just now'}
                  </div>
                </div>
              </div>
            </div>

            {/* Post Content */}
            {postContent && (
              <div style={{ padding: '0 16px 12px', fontSize: '15px', lineHeight: '1.3333' }}>
                {renderContentWithHashtags(postContent)}
              </div>
            )}

            {/* Post Images */}
            {postImages.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {postImages.map((url, imgIndex) => (
                  <div key={imgIndex} style={{ width: '100%', backgroundColor: 'var(--app-body-bg)' }}>
                    {post.isVideo ? (
                      <video
                        controls
                        src={url}
                        style={{ width: '100%', height: 'auto', display: 'block' }}
                      />
                    ) : (
                      <img
                        src={url}
                        alt={`post-${imgIndex}`}
                        style={{ width: '100%', height: 'auto', maxHeight: '600px', objectFit: 'contain', display: 'block' }}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Post Stats */}
            <div style={{ padding: '8px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '15px', color: '#65676b' }}>
              <div>{post.likes?.length || 0} {post.likes?.length === 1 ? 'like' : 'likes'}</div>
              <div>{comments.length} {comments.length === 1 ? 'comment' : 'comments'}</div>
            </div>

            {/* Post Actions */}
            <div style={{ borderTop: '1px solid #e4e6eb', padding: '4px 16px', display: 'flex', justifyContent: 'space-around' }}>
              <Button
                startIcon={isLiked ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                onClick={toggleLike}
                sx={{ 
                  color: isLiked ? '#e4405f' : '#65676b',
                  textTransform: 'none',
                  fontWeight: 600,
                  flex: 1
                }}
              >
                Like
              </Button>
              <Button
                startIcon={<ChatBubbleOutlineIcon />}
                sx={{ color: '#65676b', textTransform: 'none', fontWeight: 600, flex: 1 }}
              >
                Comment
              </Button>
              <Button
                startIcon={<ShareIcon />}
                sx={{ color: '#65676b', textTransform: 'none', fontWeight: 600, flex: 1 }}
              >
                Share
              </Button>
            </div>
          </div>

          {/* Comments Section */}
          <div style={{
            backgroundColor: 'var(--card-bg)',
            borderRadius: '8px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            padding: '20px',
            border: '1px solid var(--border-color)'
          }}>
            <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: 600 }}>Comments</h3>
            
            {/* Add Comment */}
            {authUser && (
              <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
                <Avatar src={authUser.photoURL} sx={{ width: 32, height: 32 }}>
                  {authUser.displayName?.[0]?.toUpperCase() || 'U'}
                </Avatar>
                <TextField
                  fullWidth
                  placeholder="Write a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                  size="small"
                />
                <Button variant="contained" onClick={handleAddComment}>Post</Button>
              </div>
            )}

            <Divider sx={{ mb: 2 }} />

            {/* Comments List */}
            {comments.length === 0 ? (
              <p style={{ textAlign: 'center', color: 'var(--text-tertiary)' }}>No comments yet</p>
            ) : (
              comments.map((comment) => {
                const avatarSrc = comment.userProfilePic || (comment.userGender ? getDefaultImage(comment.userGender) : null);
                return (
                  <div key={comment.id} style={{ marginBottom: '15px', display: 'flex', gap: '10px' }}>
                    <Avatar src={avatarSrc} sx={{ width: 32, height: 32 }}>
                      {comment.userName?.[0]?.toUpperCase() || 'U'}
                    </Avatar>
                    <div style={{ flex: 1 }}>
                      <div style={{ backgroundColor: 'var(--bg-secondary)', padding: '8px 12px', borderRadius: '18px' }}>
                        <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '2px', color: 'var(--text-primary)' }}>
                          {comment.userName}
                        </div>
                        <div style={{ fontSize: '15px', color: 'var(--text-primary)' }}>{comment.text}</div>
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px', paddingLeft: '12px' }}>
                        {comment.createdAt?.seconds ? new Date(comment.createdAt.seconds * 1000).toLocaleString() : 'Just now'}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostDetails;
